<script setup>
/* eslint-disable no-alert */
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import aceleresIntegrations from 'dashboard/api/aceleres/integrations';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import InstagramOperations from './InstagramOperations.vue';
import WhatsappConnectionWizard from './WhatsappConnectionWizard.vue';

const store = useStore();
const { accountScopedRoute } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const loading = ref(true);
const feedback = ref('');
const feedbackType = ref('success');
const actionLoading = reactive({});
const instances = ref([]);
const wamePool = ref({ configured: 0, available: 0 });
const sellers = ref([]);
// Providers habilitados pelo Aceleres. A lista era fixa aqui (API 1/2/Z-API)
// e oferecia a Evolution como padrão mesmo onde ela já foi descontinuada.
const whatsappProviders = ref([]);
const createWizardRef = ref(null);
const selectedInstanceId = ref(null);
const qrConnection = ref(null);
const groupConfig = ref(null);
const preloadConfig = ref(null);
const migration = ref(null);
const viewerIsManager = ref(false);
const deletionPreview = ref(null);
let qrPollingTimer = null;
let qrPollingInFlight = false;
let qrRefreshTimer = null;
let livePollingTimer = null;
let livePollingInFlight = false;
let livePollingTick = 0;

// Cards em atividade são reconferidos a cada ciclo; os ociosos custam uma
// consulta ao provedor por instância e entram a cada 6 ciclos (~1min).
const LIVE_POLL_INTERVAL_MS = 10000;
const IDLE_POLL_EVERY_TICKS = 6;
const ACTIVE_STATUSES = [
  'connected',
  'connecting',
  'failed',
  'migration_pending',
];
const IDLE_STATUSES = ['disconnected', 'unknown'];
// O QR da API 2 expira em ~40s; renovamos antes disso enquanto ninguém leu.
const QR_REFRESH_MS = 30000;

const editForm = reactive({
  display_name: '',
  connection_type: 'individual_own',
  default_assignee_user_id: '',
  connection_api: 'api1',
});

const connectionTypes = [
  { value: 'individual_own', label: 'Individual próprio' },
  { value: 'individual_company', label: 'Individual da empresa' },
  { value: 'shared', label: 'Compartilhado' },
  { value: 'company_multi', label: 'Multiusuário da empresa' },
];

const TABS = [
  { key: 'whatsapp', label: 'WhatsApp', icon: 'i-ri-whatsapp-line' },
  { key: 'social', label: 'Canais sociais', icon: 'i-lucide-share-2' },
  { key: 'apps', label: 'Apps e automações', icon: 'i-lucide-blocks' },
];
const activeTab = ref('whatsapp');
const selectedSocialChannelKey = ref(null);

const socialCatalog = [
  {
    key: 'instagram',
    label: 'Instagram',
    channelType: INBOX_TYPES.INSTAGRAM,
    route: 'instagram',
    icon: 'i-ri-instagram-line',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    channelType: INBOX_TYPES.FB,
    route: 'facebook',
    icon: 'i-ri-messenger-line',
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    channelType: INBOX_TYPES.TIKTOK,
    route: 'tiktok',
    icon: 'i-ri-tiktok-line',
  },
  {
    key: 'telegram',
    label: 'Telegram',
    channelType: INBOX_TYPES.TELEGRAM,
    route: 'telegram',
    icon: 'i-ri-telegram-line',
  },
  {
    key: 'twitter',
    label: 'X',
    channelType: INBOX_TYPES.TWITTER,
    route: 'twitter',
    icon: 'i-ri-twitter-x-line',
  },
];

const selectedInstance = computed(() =>
  instances.value.find(instance => instance.id === selectedInstanceId.value)
);

const activeWhatsappCount = computed(
  () =>
    instances.value.filter(instance => instance.status === 'connected').length
);

// Espelho do que o Aceleres registrou para estes canais. A tela do Aceleres
// mostra esse estado; aqui ele faltava, e um canal conectado no Zoraxy podia
// estar fora do espelho sem ninguém perceber.
const mirroredSocialChannels = ref([]);

const socialChannels = computed(() =>
  socialCatalog.map(channel => ({
    ...channel,
    records: (inboxes.value || []).filter(
      inbox => inbox.channel_type === channel.channelType
    ),
    mirror: mirroredSocialChannels.value.find(
      item => item.type === channel.key
    ),
  }))
);

const connectedSocialCount = computed(
  () => socialChannels.value.filter(channel => channel.records.length).length
);

const toggleSocialChannel = key => {
  selectedSocialChannelKey.value =
    selectedSocialChannelKey.value === key ? null : key;
};

const setFeedback = (message, type = 'success') => {
  feedback.value = message;
  feedbackType.value = type;
};

const setLoading = (key, value) => {
  actionLoading[key] = value;
};

const stopQrPolling = () => {
  if (qrPollingTimer) {
    window.clearInterval(qrPollingTimer);
    qrPollingTimer = null;
  }
  if (qrRefreshTimer) {
    window.clearInterval(qrRefreshTimer);
    qrRefreshTimer = null;
  }
};

const statusLabel = status => {
  const labels = {
    connected: 'Conectado',
    connecting: 'Conectando',
    disconnected: 'Desconectado',
    failed: 'Atenção necessária',
    migration_pending: 'Consolidação pendente',
    // Estado remoto indeterminado: a API respondeu sem evidência de conexão
    // nem de queda. Não é o mesmo que "desconectado" e não deve parecer um.
    unknown: 'Verificando…',
    awaiting: 'Aguardando pareamento',
  };

  return labels[status] || 'Indisponível';
};

const statusClass = status => {
  if (status === 'connected') return 'bg-n-teal-2 text-n-teal-11';
  if (['failed', 'migration_pending'].includes(status)) {
    return 'bg-n-ruby-2 text-n-ruby-11';
  }

  return 'bg-n-amber-2 text-n-amber-11';
};

const connectionTypeLabel = type =>
  connectionTypes.find(item => item.value === type)?.label || 'Não definido';

const updateInstanceStatus = (id, data) => {
  const status = data.status || data.api_status_raw;
  if (!status) return;

  instances.value = instances.value.map(instance =>
    instance.id === id
      ? {
          ...instance,
          // `migration_pending` tem rótulo e ação próprios; traduzi-lo para
          // "conectando" escondia do gestor que havia uma consolidação parada
          // esperando confirmação.
          status,
          phone_number:
            data.phone_number ||
            data.instance?.phone_number ||
            instance.phone_number,
        }
      : instance
  );
};

const loadHub = async () => {
  loading.value = true;
  try {
    const [whatsappData, sellersData] = await Promise.all([
      aceleresIntegrations.getWhatsapp(),
      aceleresIntegrations.getWhatsappSellers(),
    ]);

    instances.value = whatsappData.instances || [];
    wamePool.value = whatsappData.wame_pool || { configured: 0, available: 0 };
    whatsappProviders.value = whatsappData.providers || [];
    viewerIsManager.value = Boolean(whatsappData.viewer_is_manager);
    sellers.value = sellersData.sellers || [];
  } catch (error) {
    setFeedback(
      error.message ||
        'Não foi possível carregar as integrações. Atualize a sessão e tente novamente.',
      'error'
    );
  } finally {
    loading.value = false;
  }

  // eslint-disable-next-line no-use-before-define
  await loadAuxiliaryIntegrations();
};

// E-mail, Gmail e o mapa de instâncias saíram deste hub: a central de
// integrações do Zoraxy cobre WhatsApp e canais sociais, e os demais seguem
// no Aceleres. Sobrou uma chamada só — mas `allSettled` continua certo: uma
// falha aqui não pode derrubar a aba de WhatsApp, que é o que importa na tela.
const loadAuxiliaryIntegrations = async () => {
  const [socialResult] = await Promise.allSettled([
    aceleresIntegrations.getSocialChannels(),
  ]);

  if (socialResult.status === 'fulfilled') {
    mirroredSocialChannels.value = socialResult.value.channels || [];
  }
};

const pollWhatsappStatus = async instanceId => {
  if (qrPollingInFlight) return;

  qrPollingInFlight = true;
  try {
    const data = await aceleresIntegrations.getWhatsappStatus(instanceId);

    // 503 com `retryable` é o provedor oscilando, não uma queda: nem atualiza o
    // card nem descarta o QR — o pareamento costuma concluir mesmo assim.
    if (data.retryable) {
      setFeedback('WhatsApp instável no momento — continuamos tentando…');
      return;
    }

    updateInstanceStatus(instanceId, data);

    // Depois que o aparelho inicia o handshake, o QR já foi lido e não deve
    // continuar na tela.
    if (data.pairing_started === true || data.qr_available === false) {
      if (qrConnection.value?.instanceId === instanceId) {
        qrConnection.value = { ...qrConnection.value, pairingStarted: true };
      }
    }

    if (data.status === 'connected' || data.instance?.is_connected) {
      qrConnection.value = null;
      stopQrPolling();
      setFeedback('WhatsApp conectado com sucesso.');
      await loadHub();
    } else if (['disconnected', 'failed'].includes(data.status)) {
      qrConnection.value = null;
      stopQrPolling();
      await loadHub();
    } else if (data.status === 'migration_pending') {
      qrConnection.value = null;
      stopQrPolling();
      // eslint-disable-next-line no-use-before-define
      await loadMigration(instanceId);
    }
  } catch (error) {
    // Falhas de polling não alteram a sessão nem descartam um QR válido.
    setFeedback(
      error.message || 'Sem resposta do servidor — continuamos tentando…',
      'error'
    );
  } finally {
    qrPollingInFlight = false;
  }
};

/**
 * Renova o QR enquanto ninguém o leu. O código da API 2 expira em ~40s; sem
 * isso o usuário ficava olhando uma imagem morta até desistir.
 */
const refreshQrCode = async instanceId => {
  if (qrConnection.value?.instanceId !== instanceId) return;
  if (qrConnection.value?.pairingStarted) return;

  try {
    const data = await aceleresIntegrations.getWhatsappQr(instanceId);
    if (data.pairing_started === true) {
      qrConnection.value = { ...qrConnection.value, pairingStarted: true };
      return;
    }
    if (data.qrcode?.base64) {
      qrConnection.value = {
        ...qrConnection.value,
        base64: data.qrcode.base64,
        pairingCode: data.pairing_code || null,
      };
    }
  } catch {
    // Um QR antigo na tela é melhor que nenhum: o polling de status decide.
  }
};

const startQrPolling = instanceId => {
  stopQrPolling();
  qrPollingTimer = window.setInterval(
    () => pollWhatsappStatus(instanceId),
    3000
  );
  qrRefreshTimer = window.setInterval(
    () => refreshQrCode(instanceId),
    QR_REFRESH_MS
  );
};

/**
 * Mantém os cards da lista alinhados com o estado real do provedor.
 *
 * Sem isto o hub só mostrava a coluna `status` do banco, lida uma vez no
 * carregamento: um card marcado como desconectado por engano permanecia assim
 * até alguém clicar em "Conectar" — que apenas relê o estado remoto.
 */
const refreshLiveStatuses = async () => {
  if (livePollingInFlight || document.hidden) return;
  if (qrConnection.value) return; // o modal de QR tem o próprio polling

  livePollingInFlight = true;
  livePollingTick += 1;
  const includeIdle = livePollingTick % IDLE_POLL_EVERY_TICKS === 0;
  const pollable = includeIdle
    ? ACTIVE_STATUSES.concat(IDLE_STATUSES)
    : ACTIVE_STATUSES;

  try {
    const targets = instances.value.filter(instance =>
      pollable.includes(instance.status)
    );

    // Sequencial de propósito: cada consulta chega ao provedor remoto e o
    // paralelo transformaria uma lista grande em rajada.
    for (let index = 0; index < targets.length; index += 1) {
      if (qrConnection.value) break;
      try {
        // eslint-disable-next-line no-await-in-loop
        const data = await aceleresIntegrations.getWhatsappStatus(
          targets[index].id
        );
        if (!data.retryable) updateInstanceStatus(targets[index].id, data);
      } catch {
        // Uma instância inacessível não interrompe as demais.
      }
    }
  } finally {
    livePollingInFlight = false;
  }
};

const startLivePolling = () => {
  if (livePollingTimer) window.clearInterval(livePollingTimer);
  livePollingTimer = window.setInterval(
    refreshLiveStatuses,
    LIVE_POLL_INTERVAL_MS
  );
};

const stopLivePolling = () => {
  if (livePollingTimer) {
    window.clearInterval(livePollingTimer);
    livePollingTimer = null;
  }
};

/**
 * Recebe o resultado do assistente. A validação de nome, responsável e API
 * acontece lá dentro, passo a passo — aqui só resta o envio.
 */
const createWhatsapp = async payload => {
  setLoading('create', true);
  try {
    const data = await aceleresIntegrations.createWhatsapp({
      display_name: payload.display_name,
      connection_type: payload.connection_type,
      default_assignee_user_id: payload.default_assignee_user_id,
      provider: payload.connection_api === 'api1' ? 'api1' : 'api2',
      connection_api: payload.connection_api,
    });

    createWizardRef.value?.close();
    setFeedback(
      data.message || 'Conexão criada. Conclua o pareamento para ativá-la.'
    );
    await loadHub();

    const createdId = data.inbox?.id;
    const created = instances.value.find(instance => instance.id === createdId);
    // O assistente termina no aviso de versão; emendar o QR aqui evita que o
    // usuário precise caçar o botão "Conectar" no card recém-criado.
    if (created) {
      // eslint-disable-next-line no-use-before-define
      await connectWhatsapp(created);
    }
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading('create', false);
  }
};

const openSettings = async instance => {
  selectedInstanceId.value = instance.id;
  qrConnection.value = null;
  groupConfig.value = null;
  preloadConfig.value = null;
  migration.value = null;
  editForm.display_name = instance.name || '';
  editForm.connection_type = instance.connection_type || 'individual_own';
  editForm.default_assignee_user_id = instance.default_assignee_user_id || '';
  editForm.connection_api =
    instance.connection_api || instance.provider || 'api1';

  // eslint-disable-next-line no-use-before-define
  await Promise.all([loadPreload(instance.id), loadMigration(instance.id)]);
};

const connectWhatsapp = async instance => {
  setLoading(`connect-${instance.id}`, true);
  qrConnection.value = null;
  stopQrPolling();

  try {
    const data = await aceleresIntegrations.connectWhatsapp(instance.id, {
      connection_api: instance.connection_api || instance.provider || 'api1',
    });

    if (data.qrcode?.base64 || data.pairing_code) {
      qrConnection.value = {
        instanceId: instance.id,
        base64: data.qrcode?.base64 || null,
        pairingCode: data.pairing_code || null,
        pairingStarted: false,
      };
    }

    if (data.already_connected || data.status === 'connected') {
      setFeedback(
        data.message || 'A conexão já estava ativa e foi reconciliada.'
      );
      await loadHub();
      return;
    }

    if (data.status === 'migration_pending') {
      setFeedback(
        data.message || 'A conexão aguarda a confirmação da consolidação.',
        'error'
      );
      await openSettings(instance);
      return;
    }

    setFeedback(
      data.pairing_code
        ? `Código de pareamento: ${data.pairing_code}`
        : 'Pareamento iniciado. Leia o QR Code no WhatsApp.'
    );
    await loadHub();
    startQrPolling(instance.id);
  } catch (error) {
    // O provedor pode aceitar o pareamento e responder depois de um timeout.
    // Mantemos o polling para reconciliar sem rebaixar uma sessão válida.
    startQrPolling(instance.id);
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`connect-${instance.id}`, false);
  }
};

/**
 * Aviso da vaga reservada.
 *
 * Desconectar pelo botão devolve a vaga na hora; cair sozinha reserva por sete
 * dias, para o dono reconectar o mesmo número sem perder o lugar. Só o segundo
 * caso chega aqui, e é o único que precisa de uma saída manual.
 *
 * O texto anterior — "Vaga de conexão retida até <data> — reconecte antes disso
 * para mantê-la" — mostrava um timestamp cru e não dizia por que a vaga estava
 * retida nem que dava para abrir mão dela.
 */
const keyReleaseNotice = instance => {
  const deadline = instance?.key_release_at
    ? new Date(instance.key_release_at).getTime()
    : null;

  if (!deadline) {
    return 'Esta conexão ainda está ocupando uma vaga de WhatsApp da empresa.';
  }

  const remaining = deadline - Date.now();
  if (remaining <= 0) {
    return 'O prazo terminou: a vaga será devolvida à empresa nos próximos minutos.';
  }

  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const prazo =
    days > 0
      ? `${days} ${days === 1 ? 'dia' : 'dias'} e ${hours}h`
      : `${hours}h`;

  return `A conexão caiu sozinha. Guardamos sua vaga por mais ${prazo} para você reconectar este número; depois disso ela volta para a empresa.`;
};

const releaseWhatsappKey = async instance => {
  if (!instance) return;

  if (
    !window.confirm(
      `Devolver a vaga de ${instance.name}?\n\nEla fica disponível para outra pessoa da empresa conectar. Para usar este número de novo depois, será preciso ler o QR Code outra vez.`
    )
  ) {
    return;
  }

  setLoading(`release-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.releaseWhatsappKey(instance.id);
    setFeedback(data.message || 'Vaga devolvida.');
    await loadHub();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`release-${instance.id}`, false);
  }
};

const disconnectWhatsapp = async instance => {
  if (!instance) return;

  if (
    !window.confirm(
      `Desconectar ${instance.name}? A inbox e o histórico serão preservados.`
    )
  ) {
    return;
  }

  setLoading(`disconnect-${instance.id}`, true);
  stopQrPolling();
  if (qrConnection.value?.instanceId === instance.id) qrConnection.value = null;

  try {
    const data = await aceleresIntegrations.disconnectWhatsapp(instance.id);
    setFeedback(
      data.message || 'Conexão desconectada. A inbox foi preservada.'
    );
    await loadHub();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`disconnect-${instance.id}`, false);
  }
};

/**
 * Fecha a tentativa de pareamento sem tratá-la como desconexão.
 *
 * O botão usava `disconnect`, que apaga a sessão remota e devolve a chave ao
 * pool. Quando o usuário já tinha lido o QR e só fechava a janela, isso
 * derrubava uma conexão recém-criada. O backend decide pelo estado remoto: só
 * cancela se ninguém leu o código.
 */
const cancelConnectAttempt = async instanceId => {
  const instance = instances.value.find(item => item.id === instanceId);
  if (!instance) return;

  setLoading(`cancel-${instanceId}`, true);
  stopQrPolling();
  qrConnection.value = null;

  try {
    const data = await aceleresIntegrations.cancelConnectWhatsapp(instanceId);
    setFeedback(
      data.preserved
        ? 'O pareamento já havia começado e foi preservado.'
        : 'Tentativa de conexão cancelada.'
    );
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`cancel-${instanceId}`, false);
    await loadHub();
  }
};

const setPrimaryInstance = async instance => {
  setLoading(`primary-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.setWhatsappPrimary(instance.id);
    setFeedback(data.message || 'Instância definida como principal.');
    await loadHub();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`primary-${instance.id}`, false);
  }
};

/**
 * Remover apaga a inbox e o histórico — diferente de desconectar. Por isso a
 * prévia do backend é exibida antes da confirmação, como no Aceleres.
 */
const openDeletionPreview = async instance => {
  setLoading(`delete-preview-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.getWhatsappDeletionPreview(
      instance.id
    );
    deletionPreview.value = { instance, ...data };
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`delete-preview-${instance.id}`, false);
  }
};

const confirmDeletion = async () => {
  const instance = deletionPreview.value?.instance;
  if (!instance) return;

  setLoading(`delete-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.deleteWhatsapp(instance.id);
    setFeedback(data.message || 'Conexão removida.');
    deletionPreview.value = null;
    if (selectedInstanceId.value === instance.id)
      selectedInstanceId.value = null;
    await loadHub();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`delete-${instance.id}`, false);
  }
};

const saveSettings = async () => {
  const instance = selectedInstance.value;
  if (!instance) return;

  if (
    editForm.connection_type === 'individual_company' &&
    !editForm.default_assignee_user_id
  ) {
    setFeedback('Escolha o responsável pela conexão corporativa.', 'error');
    return;
  }

  setLoading(`settings-${instance.id}`, true);
  try {
    await aceleresIntegrations.updateWhatsappType(instance.id, {
      connection_type: editForm.connection_type,
      default_assignee_user_id:
        editForm.connection_type === 'individual_company'
          ? Number(editForm.default_assignee_user_id)
          : null,
      connection_api: editForm.connection_api,
    });
    await aceleresIntegrations.renameWhatsapp(instance.id, {
      display_name: editForm.display_name.trim(),
    });
    setFeedback(
      'Configurações salvas. A inbox e o histórico foram preservados.'
    );
    await loadHub();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`settings-${instance.id}`, false);
  }
};

const loadGroups = async id => {
  setLoading(`groups-${id}`, true);
  try {
    groupConfig.value = await aceleresIntegrations.getWhatsappGroups(id);
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`groups-${id}`, false);
  }
};

const syncGroups = async id => {
  setLoading(`groups-sync-${id}`, true);
  try {
    const data = await aceleresIntegrations.syncWhatsappGroups(id);
    setFeedback(data.message || 'Grupos sincronizados.');
    await loadGroups(id);
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`groups-sync-${id}`, false);
  }
};

const toggleGroup = jid => {
  const allowed = groupConfig.value?.config?.allowed_jids || [];
  groupConfig.value.config.allowed_jids = allowed.includes(jid)
    ? allowed.filter(item => item !== jid)
    : [...allowed, jid];
};

const saveGroups = async () => {
  const instance = selectedInstance.value;
  if (!instance || !groupConfig.value) return;

  setLoading(`groups-save-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.updateWhatsappGroups(instance.id, {
      groups_enabled: Boolean(groupConfig.value.config.groups_enabled),
      groups_mode: groupConfig.value.config.groups_mode,
      allowed_jids: groupConfig.value.config.allowed_jids || [],
    });
    groupConfig.value = data;
    setFeedback(data.message || 'Configuração de grupos salva.');
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`groups-save-${instance.id}`, false);
  }
};

const loadPreload = async id => {
  try {
    preloadConfig.value = await aceleresIntegrations.getWhatsappPreload(id);
  } catch (error) {
    setFeedback(error.message, 'error');
  }
};

const savePreload = async () => {
  const instance = selectedInstance.value;
  if (!instance || !preloadConfig.value) return;

  setLoading(`preload-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.updateWhatsappPreload(instance.id, {
      conversation_prefetch_limit: Number(
        preloadConfig.value.config.conversation_prefetch_limit
      ),
    });
    preloadConfig.value = data;
    setFeedback(data.message || 'Pré-carregamento atualizado.');
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`preload-${instance.id}`, false);
  }
};

const loadMigration = async id => {
  try {
    const data = await aceleresIntegrations.getWhatsappMigration(id);
    migration.value = data.migration || null;
  } catch (error) {
    migration.value = null;
    if (!/nenhuma consolidação pendente/i.test(error.message || '')) {
      setFeedback(error.message, 'error');
    }
  }
};

const syncSocialChannels = async () => {
  setLoading('social-sync', true);
  try {
    const data = await aceleresIntegrations.syncSocialChannels();
    mirroredSocialChannels.value =
      data.channels || mirroredSocialChannels.value;
    setFeedback(data.message || 'Canais sincronizados.');
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading('social-sync', false);
  }
};

const confirmMigration = async () => {
  const instance = selectedInstance.value;
  if (!instance || !migration.value) return;

  setLoading(`migration-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.confirmWhatsappMigration(
      instance.id,
      {
        accept_single_instance: true,
        accept_api2_exclusive: migration.value.target_provider === 'api2',
      }
    );
    migration.value = data.migration || migration.value;
    setFeedback(data.message || 'Consolidação encaminhada com segurança.');
    await loadHub();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`migration-${instance.id}`, false);
  }
};

const cancelMigration = async () => {
  const instance = selectedInstance.value;
  if (!instance || !migration.value) return;

  if (
    !window.confirm(
      'Cancelar a consolidação? A sessão e o histórico serão preservados.'
    )
  ) {
    return;
  }

  setLoading(`migration-cancel-${instance.id}`, true);
  try {
    const data = await aceleresIntegrations.cancelWhatsappMigration(
      instance.id,
      {
        accept_cancel: true,
      }
    );
    migration.value = data.migration || migration.value;
    setFeedback(data.message || 'Consolidação cancelada.');
    await loadHub();
  } catch (error) {
    setFeedback(error.message, 'error');
  } finally {
    setLoading(`migration-cancel-${instance.id}`, false);
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
  loadHub();
  startLivePolling();
});

onUnmounted(() => {
  stopQrPolling();
  stopLivePolling();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline -->
  <main class="mx-auto flex w-full max-w-7xl flex-col gap-5 px-6 pb-12">
    <section
      class="relative overflow-hidden rounded-3xl border border-n-weak bg-n-slate-12 p-6 text-white shadow-lg"
    >
      <div
        class="pointer-events-none absolute -right-20 -top-20 size-72 rounded-full bg-n-brand/30 blur-3xl"
      />
      <div
        class="pointer-events-none absolute -bottom-24 left-1/3 size-64 rounded-full bg-n-blue-8/30 blur-3xl"
      />
      <div class="relative flex flex-wrap items-end justify-between gap-5">
        <div class="max-w-3xl">
          <p
            class="text-xs font-semibold uppercase tracking-[0.22em] text-white/60"
          >
            Zoraxy control center
          </p>
          <h1 class="mt-2 text-3xl font-semibold tracking-tight">
            Integrações
          </h1>
          <p class="mt-3 text-sm leading-6 text-white/80">
            Gerencie canais, conexões e roteamento em um único lugar. As
            conexões existentes são preservadas enquanto a operação passa a ser
            centralizada no Zoraxy.
          </p>
        </div>
        <div
          class="grid grid-cols-2 gap-3 rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur-sm"
        >
          <div class="rounded-xl bg-white/10 px-4 py-3">
            <strong class="block text-2xl">{{ activeWhatsappCount }}</strong>
            <span class="text-xs text-white/65">WhatsApps ativos</span>
          </div>
          <div class="rounded-xl bg-white/10 px-4 py-3">
            <strong class="block text-2xl">{{ connectedSocialCount }}</strong>
            <span class="text-xs text-white/65">canais sociais</span>
          </div>
        </div>
      </div>
    </section>

    <nav
      class="flex w-full gap-1 overflow-x-auto rounded-full border border-n-weak bg-n-solid-1 p-1"
    >
      <button
        v-for="tab in TABS"
        :key="tab.key"
        type="button"
        class="flex flex-shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors"
        :class="
          activeTab === tab.key
            ? 'bg-n-slate-12 text-white dark:bg-n-solid-3 dark:text-n-slate-12'
            : 'text-n-slate-11 hover:bg-n-alpha-2'
        "
        @click="activeTab = tab.key"
      >
        <i :class="tab.icon" class="text-base" />
        {{ tab.label }}
      </button>
    </nav>

    <div
      v-if="feedback"
      class="flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm"
      :class="
        feedbackType === 'error'
          ? 'border-n-ruby-5 bg-n-ruby-2 text-n-ruby-11'
          : 'border-n-teal-5 bg-n-teal-2 text-n-teal-11'
      "
    >
      <i
        :class="
          feedbackType === 'error'
            ? 'i-lucide-circle-alert'
            : 'i-lucide-circle-check'
        "
        class="mt-0.5 text-base"
      />
      <span>{{ feedback }}</span>
    </div>

    <section
      v-show="activeTab === 'whatsapp'"
      class="rounded-3xl border border-n-weak bg-n-solid-1 p-5 shadow-sm"
    >
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p
            class="text-xs font-semibold uppercase tracking-[0.2em] text-n-slate-10"
          >
            Canal de atendimento
          </p>
          <h2 class="mt-1 text-xl font-semibold text-n-slate-12">WhatsApp</h2>
          <p class="mt-1 max-w-2xl text-sm text-n-slate-10">
            API 1, API 2/WAME e Z-API usam a mesma inbox e mantêm o histórico
            existente.
          </p>
        </div>
        <div class="flex flex-wrap gap-2">
          <!--
            O atalho para o cadastro nativo de WhatsApp oficial do Chatwoot foi
            retirado: ele levava para fora deste hub, para um fluxo que não passa
            pelo executor do Aceleres e portanto não cria a WhatsappInstance, não
            reserva vaga do pool nem aplica o recorte de visibilidade. Uma conexão
            criada por lá não aparecia nesta lista.
          -->
          <Button
            label="Nova conexão"
            icon="i-lucide-plus"
            @click="createWizardRef?.open()"
          />
        </div>
      </div>

      <div
        v-if="wamePool.configured > 0"
        class="mt-5 flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm"
        :class="
          wamePool.available > 0
            ? 'border-n-amber-5 bg-n-amber-2 text-n-amber-11'
            : 'border-n-ruby-5 bg-n-ruby-2 text-n-ruby-11'
        "
      >
        <i
          :class="
            wamePool.available > 0 ? 'i-lucide-info' : 'i-lucide-triangle-alert'
          "
          class="mt-0.5 text-base"
        />
        <span>
          Conexões de WhatsApp disponíveis:
          <strong>{{ wamePool.available }} de {{ wamePool.configured }}</strong
          >.
          <template v-if="wamePool.available <= 0">
            No momento não há conexões livres.
          </template>
          Para aumentar o limite de conexões, entre em contato com o
          administrador.
        </span>
      </div>

      <WhatsappConnectionWizard
        ref="createWizardRef"
        :sellers="sellers"
        :providers="whatsappProviders"
        :is-loading="Boolean(actionLoading.create)"
        @create="createWhatsapp"
      />

      <div v-if="loading" class="py-12 text-center text-sm text-n-slate-10">
        Carregando conexões…
      </div>
      <div
        v-else-if="!instances.length"
        class="mt-5 rounded-2xl border border-dashed border-n-weak p-7 text-center"
      >
        <i class="i-ri-whatsapp-line text-3xl text-n-slate-9" />
        <p class="mt-2 font-medium text-n-slate-12">
          Nenhuma conexão cadastrada
        </p>
        <p class="mt-1 text-sm text-n-slate-10">
          Crie uma conexão para iniciar o pareamento.
        </p>
        <Button
          class="mx-auto mt-4"
          size="sm"
          label="Nova conexão"
          icon="i-lucide-plus"
          @click="createWizardRef?.open()"
        />
      </div>
      <div v-else class="mt-5 grid gap-3">
        <article
          v-for="instance in instances"
          :key="instance.id"
          class="rounded-2xl border border-n-weak bg-n-background p-4 transition-shadow hover:shadow-sm"
        >
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <h3 class="truncate font-semibold text-n-slate-12">
                  {{ instance.name }}
                </h3>
                <span
                  class="rounded-full px-2.5 py-1 text-xs font-semibold"
                  :class="statusClass(instance.status)"
                  >{{ statusLabel(instance.status) }}</span
                >
                <span
                  v-if="instance.is_primary"
                  class="rounded-full bg-n-brand/15 px-2.5 py-1 text-xs font-semibold text-n-brand"
                >
                  Principal
                </span>
              </div>
              <p class="mt-1 text-sm text-n-slate-10">
                {{ instance.label || 'Aguardando pareamento' }}
              </p>
              <p class="mt-1 text-xs text-n-slate-9">
                {{ connectionTypeLabel(instance.connection_type) }} ·
                {{ instance.connection_api || instance.provider || 'API 1' }}
                <template v-if="instance.created_by">
                  · criada por {{ instance.created_by }}
                </template>
              </p>
              <p
                v-if="instance.can_release_key"
                class="mt-1 text-xs text-n-amber-11"
              >
                {{ keyReleaseNotice(instance) }}
              </p>
            </div>
            <div class="flex flex-wrap gap-2">
              <Button
                v-if="instance.status === 'migration_pending'"
                amber
                size="sm"
                label="Revisar consolidação"
                icon="i-lucide-git-merge"
                @click="openSettings(instance)"
              />
              <Button
                v-else-if="
                  !['connected', 'connecting'].includes(instance.status)
                "
                size="sm"
                label="Conectar"
                icon="i-lucide-qr-code"
                :is-loading="actionLoading[`connect-${instance.id}`]"
                @click="connectWhatsapp(instance)"
              />
              <Button
                v-else-if="instance.status === 'connected'"
                slate
                size="sm"
                label="Desconectar"
                icon="i-lucide-unplug"
                :is-loading="actionLoading[`disconnect-${instance.id}`]"
                @click="disconnectWhatsapp(instance)"
              />
              <!--
                Desabilitado enquanto o próprio connect está em voo: o card vira
                `connecting` assim que o polling reage, e dava para clicar em
                Cancelar antes de o connect terminar. O executor serializa as
                duas ações por instância e a segunda voltava 409 INSTANCE_BUSY —
                um erro na tela para algo que era só ansiedade de clique.
              -->
              <Button
                v-else
                slate
                size="sm"
                label="Cancelar tentativa"
                icon="i-lucide-x-circle"
                :disabled="actionLoading[`connect-${instance.id}`]"
                :is-loading="actionLoading[`cancel-${instance.id}`]"
                @click="cancelConnectAttempt(instance.id)"
              />
              <Button
                v-if="instance.can_release_key"
                slate
                size="sm"
                label="Devolver vaga"
                icon="i-lucide-undo-2"
                :is-loading="actionLoading[`release-${instance.id}`]"
                @click="releaseWhatsappKey(instance)"
              />
              <Button
                v-if="
                  viewerIsManager &&
                  instance.status === 'connected' &&
                  !instance.is_primary
                "
                slate
                size="sm"
                label="Tornar principal"
                icon="i-lucide-star"
                :is-loading="actionLoading[`primary-${instance.id}`]"
                @click="setPrimaryInstance(instance)"
              />
              <Button
                slate
                size="sm"
                label="Configurar"
                icon="i-lucide-settings-2"
                @click="openSettings(instance)"
              />
              <Button
                v-if="viewerIsManager"
                ruby
                size="sm"
                label="Remover"
                icon="i-lucide-trash-2"
                :is-loading="actionLoading[`delete-preview-${instance.id}`]"
                @click="openDeletionPreview(instance)"
              />
            </div>
          </div>
        </article>
      </div>

      <section
        v-if="selectedInstance"
        class="mt-5 rounded-2xl border border-n-brand/30 bg-n-brand/5 p-4"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p
              class="text-xs font-semibold uppercase tracking-[0.16em] text-n-brand"
            >
              Gerenciar conexão
            </p>
            <h3 class="mt-1 font-semibold text-n-slate-12">
              {{ selectedInstance.name }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-10">
              As alterações de configuração não removem a inbox ou o histórico.
            </p>
          </div>
          <Button
            slate
            size="sm"
            label="Fechar"
            @click="selectedInstanceId = null"
          />
        </div>

        <div class="mt-5 grid gap-4 md:grid-cols-2">
          <label class="grid gap-1 text-sm font-medium text-n-slate-12"
            >Nome<input
              v-model="editForm.display_name"
              class="rounded-xl border border-n-weak bg-n-background px-3 py-2"
          /></label>
          <label class="grid gap-1 text-sm font-medium text-n-slate-12"
            >Tipo<select
              v-model="editForm.connection_type"
              class="rounded-xl border border-n-weak bg-n-background px-3 py-2"
            >
              <option
                v-for="type in connectionTypes"
                :key="type.value"
                :value="type.value"
              >
                {{ type.label }}
              </option>
            </select></label
          >
          <label
            v-if="editForm.connection_type === 'individual_company'"
            class="grid gap-1 text-sm font-medium text-n-slate-12"
            >Responsável<select
              v-model="editForm.default_assignee_user_id"
              class="rounded-xl border border-n-weak bg-n-background px-3 py-2"
            >
              <option value="">Selecione</option>
              <option
                v-for="seller in sellers"
                :key="seller.id"
                :value="seller.id"
              >
                {{ seller.name }}
              </option>
            </select></label
          >
          <label class="grid gap-1 text-sm font-medium text-n-slate-12"
            >Tecnologia<select
              v-model="editForm.connection_api"
              class="rounded-xl border border-n-weak bg-n-background px-3 py-2"
            >
              <option value="api1">API 1</option>
              <option value="api2">API 2 / WAME</option>
              <option value="zapi">Z-API</option>
            </select></label
          >
        </div>
        <div class="mt-4 flex flex-wrap gap-2">
          <Button
            size="sm"
            label="Salvar conexão"
            :is-loading="actionLoading[`settings-${selectedInstance.id}`]"
            @click="saveSettings"
          />
          <Button
            slate
            size="sm"
            label="Grupos"
            :is-loading="actionLoading[`groups-${selectedInstance.id}`]"
            @click="loadGroups(selectedInstance.id)"
          />
          <Button
            slate
            size="sm"
            label="Sincronizar grupos"
            :is-loading="actionLoading[`groups-sync-${selectedInstance.id}`]"
            @click="syncGroups(selectedInstance.id)"
          />
        </div>

        <div
          v-if="preloadConfig"
          class="mt-5 rounded-2xl border border-n-weak bg-n-background p-4"
        >
          <h4 class="font-semibold text-n-slate-12">Pré-carregamento</h4>
          <p class="mt-1 text-sm text-n-slate-10">
            Define quantas conversas são preparadas antes da navegação.
          </p>
          <div class="mt-3 flex flex-wrap items-end gap-2">
            <label class="grid gap-1 text-sm text-n-slate-12"
              >Limite<input
                v-model.number="
                  preloadConfig.config.conversation_prefetch_limit
                "
                class="w-32 rounded-xl border border-n-weak bg-n-background px-3 py-2"
                type="number"
                min="0"
                :max="preloadConfig.config.max_conversation_prefetch_limit"
            /></label>
            <Button
              size="sm"
              label="Salvar preload"
              :is-loading="actionLoading[`preload-${selectedInstance.id}`]"
              @click="savePreload"
            />
          </div>
        </div>

        <div
          v-if="groupConfig"
          class="mt-5 rounded-2xl border border-n-weak bg-n-background p-4"
        >
          <h4 class="font-semibold text-n-slate-12">Grupos WhatsApp</h4>
          <div class="mt-3 flex flex-wrap gap-4 text-sm text-n-slate-12">
            <label class="flex items-center gap-2"
              ><input
                v-model="groupConfig.config.groups_enabled"
                type="checkbox"
              />
              Receber grupos</label
            >
            <label class="flex items-center gap-2"
              ><input
                v-model="groupConfig.config.groups_mode"
                value="all"
                type="radio"
              />
              Todos</label
            >
            <label class="flex items-center gap-2"
              ><input
                v-model="groupConfig.config.groups_mode"
                value="specific"
                type="radio"
              />
              Apenas selecionados</label
            >
          </div>
          <div
            v-if="groupConfig.available_groups?.length"
            class="mt-4 grid max-h-56 gap-2 overflow-y-auto"
          >
            <label
              v-for="group in groupConfig.available_groups"
              :key="group.group_jid"
              class="flex items-center gap-2 text-sm text-n-slate-12"
              ><input
                :checked="
                  groupConfig.config.allowed_jids.includes(group.group_jid)
                "
                type="checkbox"
                @change="toggleGroup(group.group_jid)"
              />{{ group.subject || group.group_jid }}</label
            >
          </div>
          <p v-else class="mt-3 text-sm text-n-slate-10">
            Nenhum grupo sincronizado ainda.
          </p>
          <Button
            class="mt-4"
            size="sm"
            label="Salvar grupos"
            :is-loading="actionLoading[`groups-save-${selectedInstance.id}`]"
            @click="saveGroups"
          />
        </div>

        <div
          v-if="migration"
          class="mt-5 rounded-2xl border border-n-amber-5 bg-n-amber-2 p-4"
        >
          <h4 class="font-semibold text-n-slate-12">Consolidação de conexão</h4>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ migration.error_message || migration.phase || migration.status }}
          </p>
          <p
            v-if="migration.session_preserved"
            class="mt-2 text-xs text-n-slate-11"
          >
            A sessão remota e o histórico foram preservados.
          </p>
          <div class="mt-4 flex flex-wrap gap-2">
            <Button
              v-if="migration.can_confirm"
              amber
              size="sm"
              label="Confirmar consolidação"
              :is-loading="actionLoading[`migration-${selectedInstance.id}`]"
              @click="confirmMigration"
            />
            <Button
              v-if="migration.can_cancel"
              slate
              size="sm"
              label="Cancelar consolidação"
              :is-loading="
                actionLoading[`migration-cancel-${selectedInstance.id}`]
              "
              @click="cancelMigration"
            />
          </div>
        </div>
      </section>

      <section
        v-if="qrConnection"
        class="mt-5 rounded-2xl border border-n-teal-5 bg-n-teal-2 p-5 text-center"
      >
        <template v-if="qrConnection.pairingStarted">
          <h3 class="font-semibold text-n-slate-12">Conectando ao WhatsApp…</h3>
          <p class="mt-1 text-sm text-n-slate-10">
            O código já foi lido. Não feche esta janela até a confirmação.
          </p>
        </template>
        <template v-else>
          <h3 class="font-semibold text-n-slate-12">
            Leia o QR Code no WhatsApp
          </h3>
          <p class="mt-1 text-sm text-n-slate-10">
            O código é renovado sozinho e o estado da conexão é atualizado
            automaticamente.
          </p>
          <img
            v-if="qrConnection.base64"
            class="mx-auto mt-4 size-56 rounded-xl bg-white p-2"
            :src="
              qrConnection.base64.startsWith('data:')
                ? qrConnection.base64
                : `data:image/png;base64,${qrConnection.base64}`
            "
            alt="QR Code para pareamento do WhatsApp"
          />
          <p
            v-if="qrConnection.pairingCode"
            class="mt-4 text-sm text-n-slate-11"
          >
            Ou informe o código de pareamento:
            <strong class="tracking-[0.2em]">{{
              qrConnection.pairingCode
            }}</strong>
          </p>
        </template>
        <div class="mt-4 flex justify-center gap-2">
          <Button
            slate
            size="sm"
            label="Ocultar QR"
            @click="qrConnection = null"
          />
          <Button
            slate
            size="sm"
            label="Cancelar tentativa"
            :is-loading="actionLoading[`cancel-${qrConnection.instanceId}`]"
            @click="cancelConnectAttempt(qrConnection.instanceId)"
          />
        </div>
      </section>

      <section
        v-if="deletionPreview"
        class="mt-5 rounded-2xl border border-n-ruby-5 bg-n-ruby-2 p-5"
      >
        <h3 class="font-semibold text-n-slate-12">
          Remover “{{ deletionPreview.instance.name }}”?
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          Remover é diferente de desconectar: a inbox e o histórico desta
          conexão são apagados e a ação não pode ser desfeita.
        </p>
        <p
          v-if="deletionPreview.shared_with_other"
          class="mt-3 text-sm text-n-slate-11"
        >
          Outra conexão ainda usa esta inbox: as conversas serão preservadas e
          apenas este registro é removido.
        </p>
        <ul
          v-else-if="deletionPreview.preview_available"
          class="mt-3 grid gap-1 text-sm text-n-slate-11"
        >
          <li
            class="flex justify-between gap-4 border-b border-n-ruby-5/40 py-1"
          >
            <span>Conversas atribuídas (preservadas)</span>
            <strong>{{ deletionPreview.assigned_count }}</strong>
          </li>
          <li
            class="flex justify-between gap-4 border-b border-n-ruby-5/40 py-1"
          >
            <span>Conversas sem atendente (apagadas)</span>
            <strong>
              {{ deletionPreview.unassigned_count
              }}{{ deletionPreview.count_capped ? '+' : '' }}
            </strong>
          </li>
        </ul>
        <p v-else class="mt-3 text-sm text-n-slate-11">
          Não foi possível levantar o histórico desta inbox agora. Confirme
          apenas se tiver certeza.
        </p>
        <div class="mt-4 flex flex-wrap gap-2">
          <Button
            ruby
            size="sm"
            label="Remover definitivamente"
            :is-loading="actionLoading[`delete-${deletionPreview.instance.id}`]"
            @click="confirmDeletion"
          />
          <Button
            slate
            size="sm"
            label="Manter conexão"
            @click="deletionPreview = null"
          />
        </div>
      </section>
    </section>

    <section
      v-show="activeTab === 'social'"
      class="rounded-3xl border border-n-weak bg-n-solid-1 p-5 shadow-sm"
    >
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <span
            class="inline-flex items-center gap-1.5 rounded-full bg-n-amber-2 px-2.5 py-1 text-xs font-semibold text-n-amber-11"
          >
            <i class="i-lucide-hammer text-sm" />
            Em desenvolvimento
          </span>
          <h2 class="mt-2 text-xl font-semibold text-n-slate-12">
            Canais sociais e mensageria
          </h2>
          <p class="mt-1 max-w-2xl text-sm text-n-slate-10">
            O Instagram já é gerenciado inteiramente aqui: eventos assinados na
            Meta, comentários, menções, ice breakers e presença. Facebook,
            TikTok, Telegram e X ainda não têm a configuração completa migrada —
            a gestão detalhada desses canais continua no Aceleres por enquanto.
          </p>
        </div>
        <Button
          v-if="viewerIsManager"
          slate
          size="sm"
          label="Sincronizar espelho"
          icon="i-lucide-refresh-cw"
          :is-loading="actionLoading['social-sync']"
          @click="syncSocialChannels"
        />
      </div>

      <div class="mt-5 grid gap-3">
        <article
          v-for="channel in socialChannels"
          :key="channel.key"
          class="overflow-hidden rounded-2xl border border-n-weak bg-n-background transition-shadow hover:shadow-sm"
        >
          <button
            type="button"
            class="flex w-full flex-wrap items-center justify-between gap-4 p-4 text-left"
            @click="toggleSocialChannel(channel.key)"
          >
            <div class="flex items-center gap-3">
              <i :class="channel.icon" class="text-2xl text-n-slate-12" />
              <div>
                <h3 class="font-semibold text-n-slate-12">
                  {{ channel.label }}
                </h3>
                <p class="text-xs text-n-slate-10">
                  {{
                    channel.records.length
                      ? `${channel.records.length} inbox(es) nativa(s) hoje`
                      : 'Nenhuma inbox nativa hoje'
                  }}
                  <template v-if="channel.mirror">
                    · espelho no Aceleres:
                    {{
                      channel.mirror.status === 'connected'
                        ? channel.mirror.account || 'conectado'
                        : 'não conectado'
                    }}
                  </template>
                </p>
              </div>
            </div>
            <span class="flex items-center gap-2">
              <span
                v-if="channel.key === 'instagram'"
                class="rounded-full bg-n-teal-2 px-2.5 py-1 text-xs font-semibold text-n-teal-11"
              >
                Gerenciado no Zoraxy
              </span>
              <span
                v-else
                class="rounded-full bg-n-amber-2 px-2.5 py-1 text-xs font-semibold text-n-amber-11"
              >
                Em desenvolvimento
              </span>
              <i
                class="text-n-slate-9"
                :class="
                  selectedSocialChannelKey === channel.key
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
              />
            </span>
          </button>

          <div
            v-if="selectedSocialChannelKey === channel.key"
            class="border-t border-n-weak bg-n-alpha-2 p-4"
          >
            <InstagramOperations v-if="channel.key === 'instagram'" />
            <p v-else class="text-sm text-n-slate-11">
              A configuração dedicada de {{ channel.label }} (contas, roteamento
              e automações) ainda está em desenvolvimento aqui no Zoraxy. Por
              enquanto essa integração é administrada pelo Aceleres.
            </p>
            <router-link
              class="mt-3 inline-flex"
              :to="
                accountScopedRoute('settings_inboxes_page_channel', {
                  sub_page: channel.route,
                })
              "
            >
              <Button
                slate
                size="sm"
                label="Ver inboxes nativas do Chatwoot"
                icon="i-lucide-external-link"
              />
            </router-link>
          </div>
        </article>
      </div>
    </section>

    <section
      v-show="activeTab === 'apps'"
      class="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-n-weak bg-n-alpha-2 p-5"
    >
      <div>
        <h2 class="font-semibold text-n-slate-12">Apps e automações</h2>
        <p class="mt-1 text-sm text-n-slate-10">
          Slack, Linear, Notion, Shopify e webhooks permanecem na central nativa
          de aplicações.
        </p>
      </div>
      <router-link :to="accountScopedRoute('settings_applications')">
        <Button slate label="Abrir apps e webhooks" icon="i-lucide-blocks" />
      </router-link>
    </section>
  </main>
</template>
